import {
  FloatingPortal,
  FloatingArrow,
  useDelayGroup,
} from "@floating-ui/react";
import {
  ComponentPropsWithoutRef,
  MutableRefObject,
  forwardRef,
  useContext,
  useId,
} from "react";
import { useTooltipLegacyContext } from "./useTooltipLegacyContext";
import styles from "./TooltipLegacy.module.scss";
import { useMergeRefs } from "../../hooks";
import { ThemeProvider } from "../../providers";
import cx from "classnames";
import { PortalProviderContext } from "../../providers/PortalProvider";
import { DialogContext } from "../Dialog";
import { DrawerContext } from "../Drawer";

export type TooltipLegacyContentProps = ComponentPropsWithoutRef<"div">;

export const TooltipLegacyContent = forwardRef<
  HTMLDivElement,
  TooltipLegacyContentProps
>(function TooltipLegacyContent({ style, className, ...props }, propRef) {
  const {
    context: floatingContext,
    theme,
    mode,
    root,
    ...context
  } = useTooltipLegacyContext();
  const { root: portalRoot } = useContext(PortalProviderContext);
  const dialogContext = useContext(DialogContext);
  const drawerContext = useContext(DrawerContext);

  const id = useId();

  useDelayGroup(floatingContext, { id });

  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  if (!floatingContext.open) return null;

  const contentCx = cx(styles["content-legacy"], className);

  return (
    <FloatingPortal
      root={
        root ??
        dialogContext?.ref?.current ??
        drawerContext?.ref?.current ??
        portalRoot ??
        (context.refs.reference as
          | MutableRefObject<HTMLElement | null>
          | null
          | undefined)
      }
    >
      <ThemeProvider theme={theme} mode={mode}>
        <div
          ref={ref}
          className={contentCx}
          data-anv="tooltip-content"
          style={{
            ...context.floatingStyles,
            ...style,
          }}
          onBlur={() => context.setOpen(false)}
          {...context.getFloatingProps(props)}
        >
          <FloatingArrow
            ref={context.arrowRef}
            context={floatingContext}
            className={styles["arrow-legacy"]}
          />
          {props.children}
        </div>
      </ThemeProvider>
    </FloatingPortal>
  );
});
